import React, { useCallback, useEffect, useState } from "react";

import { Button, Divider, Header } from "semantic-ui-react";

import { useIntl } from "react-intl";

import DashboardController from "react-lib/apps/IsHealth/Dashboard/DashboardController";
import ModInfoOA from "react-lib/apps/IsHealth/REG/ModInfoOA";
import UserProfileImage from "react-lib/apps/IsHealth/REG/UserProfileImage";

import PRXManager from "react-lib/apis/manager/PRXManager";

import SettingOAAccountFormBasic from "./SettingOAAccountFormBasic";
import SettingOAAccountFormDetailed from "./SettingOAAccountFormDetailed";

import CONFIG from "config/config";

type SettingOAAccountProps = {
  onEvent?: (e: any) => void;
  controller: DashboardController & {
    prxManager: PRXManager;
  };
  apiToken: string;
  division: number;
  layout?: "basic" | "detailed";
  masterData?: any;
  masterOptions?: any;
  onUpdated?: () => void;
};

const HOSPITAL_IMAGE_URL = `${process.env.PUBLIC_URL}/static/images/hospital.png`;

const SettingOAAccount: React.FC<SettingOAAccountProps> = (props) => {
  const intl = useIntl();
  const [profileImageSrc, setProfileImageSrc] = useState(HOSPITAL_IMAGE_URL);
  const [divisionProfile, setDivisionProfile] = useState<Record<string, any>>({});
  const [fileImage, setFileImage] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [healthFacilityTypeOptions, setHealthFacilityTypeOptions] = useState<Record<string, any>[]>(
    []
  );
  const [modInfoMsg, setModInfoMsg] = useState("");
  const [error, setError] = useState<Record<string, any>>({});
  const [apiErrors, setApiErrors] = useState<Record<string, string[]>>({});

  const getHealthFacilityType = useCallback(async () => {
    if (!props.controller.getHealthFacilityType) {
      return;
    }

    const [types]: [Record<string, any>[]] = await props.controller.getHealthFacilityType({
      apiToken: props.apiToken,
    });

    const options = types.map((item: any) => ({
      key: item.name,
      text: item.name,
      value: item.name,
    }));

    setHealthFacilityTypeOptions(options);
  }, [props.apiToken]);

  // Fetch division profile
  const getOADivisionProfile = useCallback(async () => {
    if (!props.division || !props.apiToken) {
      return;
    }

    setIsLoading(true);

    try {
      const [res] = await props.controller.prxManager.getOADivisionProfile({
        apiToken: props.apiToken,
        divisionId: props.division,
      });

      if (res) {
        setDivisionProfile({
          ...res,
          account_link: res.token
            ? `${CONFIG.OFFICIAL_ACCOUNT_HOST || "https://ishealth.app"}/OA/${
                res.id
              }/?openExternalBrowser=1`
            : "",
          division_address: {
            city: res.city?.id,
            district: res.district?.id,
            province: res.province?.id,
            full_address_text: res.address_text,
            zipcode: res.city?.zipcode,
            place_id: res.place_id,
            latitude: res.latitude,
            longitude: res.longitude,
          },
          business: {
            business_type: res.business_type,
            branch_type: res.branch_type,
            branch_name: res.branch_name,
            first_name: res.first_name,
            last_name: res.last_name,
            business_name: res.business_name,
            tax_payer_id: res.tax_payer_id,
          },
        });

        setProfileImageSrc(res.image_file || HOSPITAL_IMAGE_URL);
      }
    } catch (error) {
      console.error("Error fetching division profile:", error);
    } finally {
      setIsLoading(false);
    }
  }, [props.apiToken, props.division]);

  // Handle profile update
  const handleEditProfile = async () => {
    if (!divisionProfile) {
      return;
    }

    setIsLoading(true);
    setApiErrors({});

    try {
      const requiredAddressFields = ["full_address_text", "province", "district", "city"];
      const requiredBusinessFields = ["business_type", "tax_payer_id"];

      if (divisionProfile.business?.business_type === "INDIVIDUAL") {
        requiredBusinessFields.push("first_name", "last_name");
        if (divisionProfile.business?.branch_type !== "") {
          requiredBusinessFields.push("branch_type");
        }
        if (divisionProfile.business.branch_type === "BRANCH") {
          requiredBusinessFields.push("branch_name");
        }
      } else if (divisionProfile.business?.business_type === "LEGAL_ENTITY") {
        requiredBusinessFields.push("business_name");
      }

      const missingAddressFields = requiredAddressFields.filter(
        (field) => !divisionProfile.division_address?.[field]
      );
      const missingBusinessFields = requiredBusinessFields.filter(
        (field) =>
          divisionProfile.business?.[field] == null || divisionProfile.business?.[field] === ""
      );
      const errors = {
        ...Object.fromEntries(missingAddressFields.map((f) => [f, true])),
        ...Object.fromEntries(missingBusinessFields.map((f) => [f, true])),
      };

      const taxid = divisionProfile.business?.tax_payer_id || "";
      const compact = taxid.replace(/ /g, "");

      if (compact.length !== 13 || !/^\d{13}$/.test(compact)) {
        errors.tax_payer_id = true;
      }

      if (Object.keys(errors).length > 0) {
        setError(errors);

        return;
      }

      setError({});

      const [, error] = await props.controller.prxManager.patchOADivisionProfile({
        apiToken: props.apiToken,
        divisionId: props.division,
        ...divisionProfile,
        divisionName: divisionProfile.name,
        hospitalName: divisionProfile.hospital_name,
        imageFile: fileImage,
        premiumUrl: divisionProfile.premium_url,
        systemName: divisionProfile.system_name,
        oaType: divisionProfile.oa_type,
        hasStaff: divisionProfile.has_staff,
        addressText: divisionProfile.division_address?.full_address_text,
        placeId: divisionProfile.division_address?.place_id,
        ...divisionProfile.division_address,
        businessType: divisionProfile.business?.business_type,
        branchType: divisionProfile.business?.branch_type,
        branchName: divisionProfile.business?.branch_name,
        firstName: divisionProfile.business?.first_name,
        lastName: divisionProfile.business?.last_name,
        businessName: divisionProfile.business?.business_name,
        taxPayerId: divisionProfile.business?.tax_payer_id,
      });

      if (error) {
        if (props.layout === "detailed") {
          setModInfoMsg("เกิดข้อผิดพลาด");
          setApiErrors(error || {});
        }

        console.error("Error editing profile:", error);

        return;
      }

      console.log("Profile updated successfully");

      // Refresh profile data
      await getOADivisionProfile();

      props.onUpdated?.();

      if (props.layout === "detailed") {
        setModInfoMsg("บันทึกสำเร็จ");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeValue = (e: any, data: any) => {
    const name: string = data.name || "";
    const value = data.type === "checkbox" ? data.checked : data.value;

    const [first, second] = name.split(".");

    setDivisionProfile((prev) => ({
      ...prev,
      [first]: second
        ? {
            ...(prev as any)[first],
            [second]: value,
          }
        : value,
    }));
  };

  // Handle image change from UserProfileImage component
  const handleImageChange = useCallback((file: File, fileUrl: string) => {
    setFileImage(file);
    setProfileImageSrc(fileUrl);
  }, []);

  // Handle image clear from UserProfileImage component
  const handleClearImage = useCallback(() => {
    setFileImage(null);
    setProfileImageSrc(divisionProfile.image_file || HOSPITAL_IMAGE_URL);
  }, [divisionProfile.image_file]);

  // Load profile on mount and when division changes
  useEffect(() => {
    void getOADivisionProfile();
  }, [getOADivisionProfile]);

  useEffect(() => {
    void getHealthFacilityType();

    props.onEvent?.({
      message: "GetMasterData",
      params: {
        masters: [
          ["province", {}],
          ["district", {}],
          ["city", {}],
        ],
      },
    });
  }, []);

  return (
    <div style={{ height: "calc(100vh - 40px)", overflowY: "auto", padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Header style={{ color: "#4f4f4f", paddingTop: "15px" }} as="h2">
          {intl.formatMessage({ id: "ตั้งค่าบัญชี OA" })}
        </Header>
        <p
          style={{
            backgroundColor: "#d9d9d9",
            borderRadius: "4px",
            alignItems: "center",
            padding: "10px",
          }}
        >
          Official Account ID: {divisionProfile.official_account_id}
        </p>
      </div>
      <Divider style={{ marginBottom: "25px", marginTop: 0 }} />

      <div className="register-new-user" style={{ height: "auto" }}>
        <UserProfileImage
          src={profileImageSrc}
          onChange={handleImageChange}
          onClear={handleClearImage}
        />

        <div className="vl" />

        {props.layout === "detailed" ? (
          <div className="profile-info" style={{ borderLeft: 0, marginBottom: "3rem" }}>
            <SettingOAAccountFormDetailed
              data={divisionProfile}
              error={error}
              masterData={props.masterData}
              masterOptions={props.masterOptions}
              healthFacilityTypeOptions={healthFacilityTypeOptions}
              onChangeValue={handleChangeValue}
            />

            <br />

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
                disabled={isLoading}
                loading={isLoading}
                type="submit"
                onClick={handleEditProfile}
              >
                บันทึกข้อมูลการแก้ไข
              </Button>
            </div>
          </div>
        ) : (
          <SettingOAAccountFormBasic
            data={divisionProfile}
            isLoading={isLoading}
            onChangeValue={handleChangeValue}
            onSubmit={handleEditProfile}
          />
        )}
      </div>

      <ModInfoOA
        open={!!modInfoMsg}
        type={modInfoMsg.includes("บันทึกสำเร็จ") ? "success" : "error"}
        onApprove={() => {
          setModInfoMsg("");
        }}
        onClose={() => {
          setModInfoMsg("");
        }}
        apiErrors={apiErrors}
      />
    </div>
  );
};

export default SettingOAAccount;
